/**
 * Workshop: CompanionDisconnectModal — shown when the browser WebSocket
 * connection to the backend is lost.
 *
 * Two modes:
 *  1. "ws-reconnecting" — blocking modal with spinner + attempt counter.
 *  2. "ws-failed" — blocking modal with failure message + troubleshooting.
 *
 * Auto-dismisses when the WS connection is restored.
 */

import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";

type ModalMode = "ws-reconnecting" | "ws-failed";

interface CompanionDisconnectModalProps {
  mode: ModalMode;
  reconnectAttempt?: number;
  maxRetries?: number;
  onRetry?: () => void;
  onReload?: () => void;
}

export function CompanionDisconnectModal({
  mode,
  reconnectAttempt = 3,
  maxRetries = 10,
  onRetry,
  onReload,
}: CompanionDisconnectModalProps) {
  const [troubleshootingOpen, setTroubleshootingOpen] = useState(
    mode === "ws-failed",
  );

  const isFailed = mode === "ws-failed";

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center"
      data-testid="companion-disconnect-modal"
    >
      {/* Backdrop — non-dismissable */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        aria-hidden="true"
        data-testid="companion-disconnect-modal-backdrop"
      />

      {/* Card */}
      <div
        className={cn(
          "relative z-10 w-full max-w-sm rounded-xl",
          "border border-glass-stroke bg-glass-fill-dense shadow-glass backdrop-blur-md",
          "p-5",
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="companion-disconnect-title"
        aria-describedby="companion-disconnect-status"
        data-testid="companion-disconnect-modal-card"
      >
        {/* Status icon */}
        <div className="mb-4 flex items-center gap-3">
          {!isFailed ? (
            <div
              className="flex h-9 w-9 items-center justify-center rounded-full bg-warning-muted"
              data-testid="companion-disconnect-modal-icon-reconnecting"
            >
              <Icons.Spinner className="h-4 w-4 animate-spin text-warning" />
            </div>
          ) : (
            <div
              className="flex h-9 w-9 items-center justify-center rounded-full bg-danger-muted"
              data-testid="companion-disconnect-modal-icon-failed"
            >
              <Icons.WifiOff className="h-4 w-4 text-danger" />
            </div>
          )}

          <div className="flex flex-col">
            <h2
              id="companion-disconnect-title"
              className="text-[13px] font-semibold text-primary"
            >
              {isFailed ? "Connection Lost" : "Reconnecting..."}
            </h2>
            <p
              id="companion-disconnect-status"
              className="text-[11px] text-secondary"
            >
              {isFailed
                ? "Could not reach the server after multiple attempts"
                : `Attempting to reconnect... (${reconnectAttempt}/${maxRetries})`}
            </p>
          </div>
        </div>

        {/* Troubleshooting section */}
        <div
          className={cn(
            "mb-4 overflow-hidden rounded-lg border border-glass-stroke bg-glass-fill transition-all duration-200",
            troubleshootingOpen ? "max-h-60" : "max-h-0 border-transparent",
          )}
          data-testid="companion-disconnect-modal-troubleshooting"
        >
          <div className="p-3">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted">
              Troubleshooting
            </p>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-start gap-2">
                <Icons.Terminal className="mt-0.5 h-3 w-3 shrink-0 text-muted" />
                <div>
                  <p className="text-[10px] text-secondary">
                    Restart the companion
                  </p>
                  <code className="text-[9px] text-muted">
                    denker-companion run
                  </code>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Icons.Info className="mt-0.5 h-3 w-3 shrink-0 text-muted" />
                <div>
                  <p className="text-[10px] text-secondary">
                    Check companion status
                  </p>
                  <code className="text-[9px] text-muted">
                    denker-companion status
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Toggle troubleshooting (only when reconnecting) */}
        {!isFailed && (
          <button
            type="button"
            className="mb-4 flex items-center gap-1 text-[10px] text-muted transition-colors hover:text-secondary"
            onClick={() => setTroubleshootingOpen((o) => !o)}
            data-testid="companion-disconnect-modal-troubleshooting-toggle"
          >
            <Icons.ChevronRight
              className={cn(
                "h-3 w-3 transition-transform duration-200",
                troubleshootingOpen && "rotate-90",
              )}
            />
            Troubleshooting
          </button>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onReload?.()}
            data-testid="companion-disconnect-modal-reload"
          >
            <Icons.RotateCcw className="h-3 w-3" />
            Reload Page
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onRetry?.()}
            data-testid="companion-disconnect-modal-retry"
          >
            <Icons.Wifi className="h-3 w-3" />
            Retry Now
          </Button>
        </div>
      </div>
    </div>
  );
}

export type { CompanionDisconnectModalProps, ModalMode };
